import { AccountCircleOutlined, Store } from "@mui/icons-material";
import Lock from "@mui/icons-material/Lock";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface TrainingCardProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  children: ReactNode;
}

const TrainingCard = ({
  icon,
  title,
  subtitle,
  children,
}: TrainingCardProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        padding: theme.tokens.FuelSpacing6,
        border: `1px solid ${theme.tokens.FuelColorBorderBase}`,
        borderRadius: theme.tokens.FuelBorderRadiusMd,
        flex: 1,
        minWidth: "300px",
        maxWidth: "500px",
      }}
    >
      <Box
        sx={{
          marginBottom: theme.tokens.FuelSpacing3,
          display: "flex",
          alignItems: "center",
          gap: theme.tokens.FuelSpacing2,
        }}
      >
        <Box>{icon}</Box>
        <Typography
          sx={{
            fontSize: theme.tokens.FuelFontSizeHeading3,
            fontWeight: 600,
            fontStyle: theme.tokens.FuelFontWeightSemibold,
            marginBottom: theme.tokens.FuelSpacing2,
          }}
          component="h3"
        >
          {title}
        </Typography>
      </Box>
      <Typography
        sx={{
          marginBottom: theme.tokens.FuelSpacing4,
          color: theme.tokens.FuelColorTextSecondary,
        }}
      >
        {subtitle}
      </Typography>
      {children}
    </Box>
  );
};

export const AccessRestrictedNoRoles = () => {
  const theme = useTheme();
  const { t } = useTranslation<"common">("common");

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
      data-testid="access-restricted-no-roles-page"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          padding: theme.tokens.FuelSpacing8,
          maxWidth: "1200px",
          width: "100%",
        }}
      >
        <Lock
          sx={{
            fontSize: "64px",
            color: theme.tokens.FuelColorTextSecondary,
            marginBottom: theme.tokens.FuelSpacing4,
          }}
          data-testid="lock-icon"
        />

        <Typography
          sx={{
            textAlign: "center",
            paddingBottom: theme.tokens.FuelSpacing6,
            fontWeight: 600,
            fontStyle: theme.tokens.FuelFontWeightBold,
            fontSize: theme.tokens.FuelFontSizeHeading1,
          }}
          component="h1"
        >
          {t("accessRestricted.noRoles.heading")}
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            marginBottom: theme.tokens.FuelSpacing2,
            fontWeight: 600,
            fontStyle: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading3,
          }}
        >
          {t("accessRestricted.noRoles.body.line1")}
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            marginBottom: theme.tokens.FuelSpacing8,
            color: theme.tokens.FuelColorTextSecondary,
          }}
        >
          {t("accessRestricted.noRoles.body.line2")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: theme.tokens.FuelSpacing4,
            flexWrap: "wrap",
          }}
        >
          <TrainingCard
            icon={
              <Store
                sx={{
                  fontSize: "32px",
                  color: "#005DB1",
                }}
                data-testid="dealer-icon"
              />
            }
            title={t("accessRestricted.noRoles.dealer.title")}
            subtitle={t("accessRestricted.noRoles.dealer.subtitle")}
          >
            <Box
              sx={{
                display: "flex",
                gap: theme.tokens.FuelSpacing8,
              }}
            >
              <Typography
                component="a"
                href="https://sway.cloud.microsoft/N060i0uUf2CrXfvU?ref=Link"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.tokens.FuelColorBrandPrimary,
                  fontWeight: 600,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                data-testid="dealer-training-link-english"
              >
                {t("accessRestricted.noRoles.dealer.link.english")}
              </Typography>
              <Typography
                component="a"
                href="https://sway.cloud.microsoft/h826JTlKKRPgGHYd?ref=Link&loc=mysways"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.tokens.FuelColorBrandPrimary,
                  fontWeight: 600,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                data-testid="dealer-training-link-spanish"
              >
                {t("accessRestricted.noRoles.dealer.link.spanish")}
              </Typography>
            </Box>
          </TrainingCard>

          <TrainingCard
            icon={
              <AccountCircleOutlined
                sx={{
                  fontSize: "32px",
                  color: theme.tokens.FuelColorBrandPrimary,
                }}
                data-testid="employee-icon"
              />
            }
            title={t("accessRestricted.noRoles.jdEmployee.title")}
            subtitle={t("accessRestricted.noRoles.jdEmployee.subtitle")}
          >
            <Typography
              component="a"
              href="https://johndeere.service-now.com/now/nav/ui/classic/params/target/kb_view.do%3Fsys_kb_id%3Ddc703dea93c487d43a8d3ca47aba10d7"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.tokens.FuelColorBrandPrimary,
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              data-testid="jd-employee-training-link"
            >
              {t("accessRestricted.noRoles.jdEmployee.link")}
            </Typography>
          </TrainingCard>
        </Box>
      </Box>
    </Box>
  );
};
